/**
 * DecisionUnitPanel — 10I
 *
 * Renders a Decision Unit as a coherent object — not just sessions, tracks, or
 * proposals, but the whole thing with its accumulated state.
 *
 * Shows:
 *   1. Identity — configuration name, context type, object type, output shape
 *   2. Frame status — the four frame stages as a compact status strip
 *   3. Decision status — scoring completeness and ranking state
 *   4. Catchball state (if active)
 *   5. History accordion (collapsed by default) — provenance
 *   6. Primary action CTA — derived from computeStageNextStep, never hardcoded
 *
 * Canon reference: DOCS_V4/00-canon/10I-cursor-decision-unit-runtime.md
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Award,
  BarChart3,
  RefreshCw,
  AlertCircle,
  Users,
  Package,
  ListChecks,
  Sliders,
  Send,
} from 'lucide-react';
import { useDecisionUnit } from '../../hooks/prioritization/useDecisionUnit';
import { computeStageNextStep } from '../../lib/nextStepEngine';
import type { StageCompletionStatus } from '../../types/prioritization/context';
import type { DecisionUnit } from '../../types/prioritization/decisionUnit';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DecisionUnitPanelProps {
  contextId: string;
  /** The track this panel sits on; the unit is read against its pool, criteria and sessions. */
  trackId?: string | null;
  activeCycleId?: string;
  /** When true, collapse the history section by default */
  compact?: boolean;
}

// ─── Stage strip ──────────────────────────────────────────────────────────────

const FRAME_STAGES: Array<{
  key: keyof StageCompletionStatus;
  label: string;
  shortLabel: string;
  icon: React.FC<{ className?: string }>;
}> = [
  { key: 'participation',      label: 'Participation',        shortLabel: 'Participation', icon: Users },
  { key: 'pool_formation',     label: 'Pool Formation',       shortLabel: 'Pool',          icon: Package },
  { key: 'criteria_selection', label: 'Criteria Selection',   shortLabel: 'Criteria',      icon: ListChecks },
  { key: 'criteria_expression', label: 'Criteria Expression', shortLabel: 'Expression',    icon: Sliders },
];

const DECISION_STAGES: Array<{
  key: keyof StageCompletionStatus;
  label: string;
  shortLabel: string;
  icon: React.FC<{ className?: string }>;
}> = [
  { key: 'item_scoring',  label: 'Item Scoring',  shortLabel: 'Scoring',  icon: BarChart3 },
  { key: 'final_ranking', label: 'Final Ranking', shortLabel: 'Ranking',  icon: Award },
];

function StageChip({
  label,
  complete,
  icon: Icon,
}: {
  label: string;
  complete: boolean;
  icon: React.FC<{ className?: string }>;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        complete
          ? 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
          : 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-ter)]'
      }`}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      {label}
      {complete ? (
        <CheckCircle2 className="h-3 w-3 text-[var(--green)] flex-shrink-0" />
      ) : (
        <Circle className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
      )}
    </span>
  );
}

// ─── Ball holder badge ─────────────────────────────────────────────────────────

function BallHolderBadge({ holder }: { holder?: string }) {
  if (!holder) return null;
  const map: Record<string, { label: string; cls: string }> = {
    evaluator: { label: 'Ball is with: Evaluator', cls: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
    worker:    { label: 'Ball is with: Worker',    cls: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
    in_transit: { label: 'Ball in transit',        cls: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]' },
  };
  const cfg = map[holder] ?? map.in_transit;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${cfg.cls}`}>
      <Send className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

// ─── Provenance row ───────────────────────────────────────────────────────────

function ProvenanceRow({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-[var(--rule)] last:border-0">
      <span className="text-xs text-[var(--text-ter)] flex-shrink-0 min-w-[90px]">{label}</span>
      <span className="min-w-0 break-words text-xs text-[var(--text-pri)] text-right leading-relaxed">
        {value}
        {sub && <span className="block text-[var(--text-ter)]">{sub}</span>}
      </span>
    </div>
  );
}

// ─── Loaded state ─────────────────────────────────────────────────────────────

function DecisionUnitPanelLoaded({
  unit,
  activeCycleId,
  compact,
}: {
  unit: DecisionUnit;
  activeCycleId?: string;
  compact?: boolean;
}) {
  const [historyOpen, setHistoryOpen] = useState(!compact);
  // Phone only: the whole panel folds into one row so the page's own header
  // and tabs come first. md and up never read this — the panel is always open.
  const [phoneOpen, setPhoneOpen] = useState(false);

  const stageNextStep = computeStageNextStep(unit.stageCompletion, unit.contextId, activeCycleId);

  const configName = unit.configuration?.name;
  const contextLabel =
    unit.context.context_type === 'cycle'
      ? 'Cycle'
      : unit.context.context_type === 'group'
      ? 'Group'
      : 'Personal';
  const objectTypeLabel = unit.configuration?.object_types?.[0] ?? null;
  const outputShapeLabel = unit.configuration?.output_shapes?.[0] ?? null;

  const hasCatchball = !!unit.catchballState?.active;
  const hasHistory =
    unit.provenance.sessions.length > 0 ||
    unit.provenance.catchballLoops.length > 0 ||
    unit.provenance.governanceEvents.length > 0;

  const scoringLabel =
    unit.decision.scoring.scoredItemCount > 0
      ? `${unit.decision.scoring.scoredItemCount} item${unit.decision.scoring.scoredItemCount !== 1 ? 's' : ''} scored`
      : 'No values entered yet';

  const rankingLabel = unit.decision.ranking.hasPublishedRanking
    ? `Published${unit.decision.ranking.publishedAt ? ` · ${new Date(unit.decision.ranking.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}`
    : 'Not yet produced';

  const allStages = [...FRAME_STAGES, ...DECISION_STAGES];
  const stagesDone = allStages.filter(({ key }) => unit.stageCompletion[key]).length;
  const unitName = configName ?? unit.context.name ?? contextLabel;

  return (
    <div>
      {/* Phone: one row that opens the panel. On a phone the panel stacked
          above the page and pushed the page's own header and tabs about one
          screen down (feature-function audit #36). md and up never draw it. */}
      <button
        type="button"
        onClick={() => setPhoneOpen((o) => !o)}
        aria-expanded={phoneOpen}
        className="md:hidden flex w-full items-center gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
            Decision Unit
          </span>
          <span className="block truncate text-sm font-medium text-[var(--text-pri)]">{unitName}</span>
        </span>
        <span className="flex-shrink-0 text-xs text-[var(--text-ter)]">
          {unit.complete ? 'Complete' : `${stagesDone} of ${allStages.length} stages`}
          {hasCatchball && ' · Catchball'}
        </span>
        {phoneOpen ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
        )}
      </button>

      <div
        className={`${phoneOpen ? 'border-t-0' : 'hidden'} md:block md:border-t border border-[var(--rule)] bg-[var(--bg-raised)] divide-y divide-[var(--rule)]`}
      >
        {/* ── 1. Identity ────────────────────────────────────────────────── */}
        <div className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
            Decision Unit
          </p>
          <h3 className="mt-1 text-base font-semibold text-[var(--text-pri)] leading-tight">
            {unitName}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--text-ter)]">
            <span>{contextLabel}</span>
            {objectTypeLabel && (
              <>
                <span className="text-[var(--text-ter)]">·</span>
                <span className="capitalize">{objectTypeLabel}</span>
              </>
            )}
            {outputShapeLabel && (
              <>
                <span className="text-[var(--text-ter)]">·</span>
                <span className="capitalize">{outputShapeLabel}</span>
              </>
            )}
            {unit.complete && (
              <>
                <span className="text-[var(--text-ter)]">·</span>
                <span className="flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                  <CheckCircle2 className="h-3 w-3" />
                  Complete
                </span>
              </>
            )}
          </div>
        </div>

        {/* ── 2. Frame status ────────────────────────────────────────────── */}
        <div className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2.5">
            Frame
          </p>
          <div className="flex flex-wrap gap-2">
            {FRAME_STAGES.map(({ key, shortLabel, icon }) => (
              <StageChip
                key={key}
                label={shortLabel}
                complete={unit.stageCompletion[key]}
                icon={icon}
              />
            ))}
          </div>
        </div>

        {/* ── 3. Decision status ─────────────────────────────────────────── */}
        <div className="p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2.5">
            Decision
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {DECISION_STAGES.map(({ key, shortLabel, icon }) => (
              <StageChip
                key={key}
                label={shortLabel}
                complete={unit.stageCompletion[key]}
                icon={icon}
              />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-[var(--text-sec)]">
            <div className="bg-[var(--surface)] px-3 py-2">
              <span className="block text-[var(--text-ter)] mb-0.5">Scoring</span>
              <span className="font-medium text-[var(--text-pri)]">{scoringLabel}</span>
            </div>
            <div className="bg-[var(--surface)] px-3 py-2">
              <span className="block text-[var(--text-ter)] mb-0.5">Ranking</span>
              <span className={`font-medium ${unit.decision.ranking.hasPublishedRanking ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
                {rankingLabel}
              </span>
            </div>
          </div>
        </div>

        {/* ── 4. Catchball state ─────────────────────────────────────────── */}
        {hasCatchball && (
          <div className="p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2.5">
              Catchball
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <BallHolderBadge holder={unit.catchballState?.ballHolder} />
            </div>
            {unit.provenance.catchballLoops.length > 0 && (() => {
              const latest = [...unit.provenance.catchballLoops].reverse()[0];
              return (
                <div className="mt-2.5 space-y-1.5">
                  {latest.intent && (
                    <p className="text-xs text-[var(--text-sec)] italic">
                      &ldquo;{latest.intent}&rdquo;
                    </p>
                  )}
                  {latest.tossedAt && (
                    <p className="text-xs text-[var(--text-ter)]">
                      Tossed{' '}
                      {new Date(latest.tossedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── 5. History accordion ──────────────────────────────────────── */}
        {hasHistory && (
          <div>
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--surface)] transition-colors"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                History
                <span className="ml-1.5 rounded-full bg-[var(--hover-tint)] px-1.5 py-0.5 text-[var(--text-ter)] font-normal">
                  {unit.provenance.sessions.length + unit.provenance.catchballLoops.length + unit.provenance.governanceEvents.length}
                </span>
              </span>
              {historyOpen ? (
                <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              )}
            </button>

            {historyOpen && (
              <div className="px-4 pb-4 space-y-3">
                {/* Sessions */}
                {unit.provenance.sessions.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-[var(--text-ter)] mb-1.5">Sessions</p>
                    <div className="space-y-0">
                      {unit.provenance.sessions.map((s) => (
                        <ProvenanceRow
                          key={s.sessionId}
                          label={s.sessionType}
                          value={s.state}
                          sub={s.completedAt
                            ? new Date(s.completedAt).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                              })
                            : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Catchball loops */}
                {unit.provenance.catchballLoops.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-[var(--text-ter)] mb-1.5">Catchball Loops</p>
                    <div className="space-y-0">
                      {unit.provenance.catchballLoops.map((l) => (
                        <ProvenanceRow
                          key={l.loopId}
                          label={l.evaluatorDecision ?? l.state}
                          value={l.intent ?? 'No intent recorded'}
                          sub={l.tossedAt
                            ? new Date(l.tossedAt).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                              })
                            : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Governance events */}
                {unit.provenance.governanceEvents.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-[var(--text-ter)] mb-1.5">Governance Events</p>
                    <div className="space-y-0">
                      {unit.provenance.governanceEvents.map((e, i) => (
                        <ProvenanceRow
                          key={i}
                          label={e.eventType}
                          value={e.description ?? e.eventType}
                          sub={new Date(e.occurredAt).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── 6. Primary action CTA ─────────────────────────────────────── */}
        {stageNextStep && (
          <div className="p-4">
            <Link
              to={stageNextStep.action.target}
              className="flex items-center justify-between w-full bg-[var(--slate)] hover:bg-[var(--slate)] text-white px-4 py-3 text-sm font-medium transition-colors"
            >
              <span>{stageNextStep.title}</span>
              {stageNextStep.stage === 'final_ranking' ? (
                <Award className="h-4 w-4 flex-shrink-0" />
              ) : (
                <ArrowRight className="h-4 w-4 flex-shrink-0" />
              )}
            </Link>
            <p className="mt-1.5 text-xs text-[var(--text-ter)] px-1">{stageNextStep.description}</p>
          </div>
        )}

        {unit.complete && !stageNextStep && (
          <div className="px-4 py-3 flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-medium">All six stages complete</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

export function DecisionUnitPanel({ contextId, trackId, activeCycleId, compact = false }: DecisionUnitPanelProps) {
  const { decisionUnit, isLoading, error, refresh } = useDecisionUnit(contextId, trackId);

  if (isLoading) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3 md:p-6 flex items-center gap-3">
        <RefreshCw className="h-4 w-4 text-[var(--text-ter)] animate-spin" />
        <span className="text-sm text-[var(--text-ter)]">Loading Decision Unit…</span>
      </div>
    );
  }

  if (error || !decisionUnit) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 flex items-start gap-3">
        <AlertCircle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[var(--text-sec)]">{error ?? 'Decision Unit not available'}</p>
          {error && (
            <button
              type="button"
              onClick={refresh}
              className="mt-2 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <DecisionUnitPanelLoaded
      unit={decisionUnit}
      activeCycleId={activeCycleId}
      compact={compact}
    />
  );
}

export default DecisionUnitPanel;
